import React, { useMemo } from 'react';

import Pen from '@ast/magma/components/icon/icons/Pen';
import Star from '@ast/magma/components/icon/icons/Star';

import { Button } from '@ast/magma/components/button';
import { Menu } from '@ast/magma/components/menu';

import { ContactType } from '@app/queryTyping';

import {
  FieldInfoGroupFieldFragment as FieldInfoGroupField,
} from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import {
  FieldComponent,
  FieldLocator,
} from '@app/common/configurable-wizards/types';
import { isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';

import { ActionListItem } from '@app/widgets/contacts-and-settings/components/ActionListItem';
import { DeleteModal } from '@app/widgets/contacts-and-settings/components/DeleteModal';
import { SectionHeader, SettingsSection } from '@app/widgets/contacts-and-settings/components/SettingsSection';
import { SubmitModal } from '@app/widgets/contacts-and-settings/components/SubmitModal';
import { useContactFieldMeta } from '@app/widgets/contacts-and-settings/hooks/useContactFieldMeta';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

export const EmailFieldListItemDetails: FieldComponent<FieldInfoGroupField> = ({ field }) => {
  const { t } = useUserSettingsTranslation();
  const { fieldMeta, isReadonly, isPrimary } = useContactFieldMeta(field);

  const email: string = fieldMeta?.PrimaryEmail?.sValue ?? '';

  const contact = useMemo(() => ({
    contactId: fieldMeta?.UniqueId?.sValue,
    contactType: ContactType.EMAIL,
  }), [fieldMeta]);

  return (
    <>
      <SettingsSection
        sectionHeader={(
          <>
            <SectionHeader
              isPrimary={isPrimary}
              title={email}
              subTitle={t(
                'email-field-list-item-details.primary-label|Primary email label',
                'Primary email',
              )}
            />
            {!isReadonly && (
              <SubmitModal
                field={field}
                successMessage={t(
                  'email-field-list-item-details.update-dialog-success.text|Text for a title on Edit email address dialog',
                  'Your email address has been successfully changed',
                )}
                actionButton={(
                  <Button
                    look="icon"
                    icon={<Pen />}
                    aria-label={t(
                      'email-field-list-item-details.action-button.aria-label|Tooltip text for edit email address action button',
                      'Edit email address',
                    )}
                  />
                )}
                dialogOptions={{
                  title: t(
                    'email-field-list-item-details.edit-dialog.title-label|Text for a title on Edit email address dialog',
                    'Edit email address',
                  ),
                  showClose: {
                    // eslint-disable-next-line i18next/no-literal-string
                    'aria-label': t(
                      'email-field-list-item-details.edit-dialog.close-button.aria-label|Text for a close button on Edit email address dialog',
                      'Close edit email dialog',
                    ),
                  },
                }}
              />
            )}
            {!isPrimary && !isReadonly && (
              <DeleteModal
                contact={contact}
                successMessage={t(
                  'email-field-list-item-details.delete-dialog.success-message|Message text for successful delete email address',
                  'Your email address has been successfully removed',
                )}
                dialogOptions={{
                  title: t(
                    'email-field-list-item-details.delete-dialog.title|Delete email address dialog title',
                    'Delete email address',
                  ),
                }}
              >
                {t(
                  // eslint-disable-next-line i18next/no-literal-string
                  'email-field-list-item-details.delete-dialog.message|Delete email address dialog message',
                  'Are you sure you want to delete email address <strong>{{email}}</strong> from your contact information?',
                  { email },
                )}
              </DeleteModal>
            )}
          </>
        )}
        sectionNote={t(
          'email-field-list-item-details.email-usage-explanation.text|Email usage description',
          'Your email can be used to deliver important notification, help you to retrieve username or password, and more',
        )}
      />
      {!isReadonly && (
        <SettingsSection
          sectionHeader={t(
            'email-field-details.preferences.title|Email address preferences title',
            'Preferences',
          )}
        >
          <Menu>
            <ActionListItem
              data-stable-name="SetAsPrimaryEmailButton"
              data={t(
                'email-field-list-item-details.set-primary-button|Set email address as primary button',
                'Set as primary email',
              )}
              getItemIcon={() => <Star />}
              aria-disabled={isPrimary}
            >
              <SubmitModal
                // eslint-disable-next-line no-underscore-dangle
                field={field}
                successMessage={t(
                  'email-field-list-item-details.mark-primary-dialog-success.text|Text for a title on set primary email address dialog',
                  'Your email address has been successfully marked as primary',
                )}
                markAsPrimary
                dialogOptions={{
                  title: t(
                    'email-field-list-item-details.mark-primary-dialog-title.label|Text for a title on Edit email address dialog',
                    'Set primary email address',
                  ),
                  showClose: {
                    // eslint-disable-next-line i18next/no-literal-string
                    'aria-label': t(
                      'email-field-list-item-details.set-primary-dialog-close-button.aria-label|Text for a close button on Mark as primary email address dialog',
                      'Close set primary email dialog',
                    ),
                  },
                }}
              >
                {t(
                  // eslint-disable-next-line i18next/no-literal-string
                  'email-field-list-item-details.mark-primary-dialog.title-label|Text for a title on Edit email address dialog',
                  'Are you sure you want to make email address <strong>{{email}}</strong> as primary?',
                  { email },
                )}
              </SubmitModal>
            </ActionListItem>
          </Menu>
        </SettingsSection>
      )}
    </>
  );
};

export const DetailEmailFieldInfoGroupItemLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id === 'EmailFieldGroup'
    ? EmailFieldListItemDetails
    : undefined
);
